import { AfterViewInit, Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BlogService } from 'src/app/blog.service';
import { MetaSetterService } from 'src/app/general/meta-setter.service';
import { LocalisationService } from 'src/app/localisation.service';
import { SPORTS } from "src/app/sports";
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-best-odds',
  templateUrl: './best-odds.component.html',
  styleUrls: ['./best-odds.component.scss']
})
export class BestOddsComponent implements OnInit, AfterViewInit {

	userState: string = "";
  pageMeta: Record<string, any>;
  sportMeta: Record<string, any>;
  league: string;
  trustedDescription: SafeHtml;
	trustedCopy: SafeHtml;

  environment = environment;

  constructor(
    private router: Router,
    private blogService: BlogService,
    private route: ActivatedRoute,
    private sanitiser: DomSanitizer,
    private metaSetter: MetaSetterService,
    localisationService: LocalisationService,
  ) {

    this.league = this.route.snapshot.queryParamMap.get("league");


    // if league isn't provided, pick the highest-priority active sport that has Best Odds available, and redirect to the Best Odds page for that
    if (!this.league) {
      const availableSports = localisationService.sportPrioritySort(SPORTS).filter(s => s.active && s.navigablePages.some(p => p.path === "/best-odds" && !p.hideCondition));

      if (availableSports.length > 0) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: availableSports[0].navigablePages.find(p => p.path === "/best-odds").queryParams,
          queryParamsHandling: "merge",
          replaceUrl: true,
        })
      } else {
        // TODO
      }
    }

    // redirect catch for league with changed code ("liga-mx" becomes "lmx")
    if (this.league === "liga-mx") {
      this.league = "lmx";
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          league: "lmx",
        },
        queryParamsHandling: "merge",
        replaceUrl: true,
      })
    }
  }

  ngOnInit(): void {
    

		this.route.queryParams.subscribe((data) => {
			if (data.league && this.league !== data.league) {
				this.league = data.league;

				this.initialiseBestOddsBlock();

			}
		});
  }

  ngAfterViewInit(): void {
    if (typeof document !== "undefined") {
      this.blogService.userLocationData$
        .subscribe(loc => {
          if (loc && loc.state) {
            this.userState = loc.state

          } else {
            // default to New Jersey if user location data can't be retrieved
            this.userState = "nj";
          }
        })
    }

    this.initialiseBestOddsBlock();
  }

  initialiseBestOddsBlock() {

    this.blogService.getAppSettings("best-odds")
      .subscribe(appSet => {
        this.pageMeta = appSet;

        
        if (typeof document !== "undefined" && typeof window !== "undefined") {
          // external script from Metabet that causes body of Best Odds page to be populated
              
          if (Array.from(document.scripts).some(s => s.src === `https://go.metabet.io/js/global.js?siteID=dimers`)) {
            const oldScript = Array.from(document.scripts).find(s => s.src === `https://go.metabet.io/js/global.js?siteID=dimers`);
            oldScript.parentNode.removeChild(oldScript);
          }
        
        
          let js, fjs = document.getElementsByTagName("script")[0];
          js = document.createElement("script");
          js.src = `https://go.metabet.io/js/global.js?siteID=dimers`;
          js.defer = true;
          js.onload = () => {
            // script is supposed to automatically run, but relies on DOMContentLoaded not having already fired, so inner method
            // must be called directly
            window["mb_DOMContentLoaded"]()
      
            // when sport is changed in dropdown inside block, navigate to page matching that sport
            if (document.getElementById('metabet-odds-board-sport')) {
              document
                .getElementById('metabet-odds-board-sport')
                .addEventListener('change', (event) => this.selectLeague(event))
            }
          }
          fjs.parentNode.insertBefore(js, fjs);
        }


        this.blogService.getBestOddsMeta()
          .subscribe(bom => {
            if (bom.some(m => m.sport_league.toUpperCase() === this.league.toUpperCase())) {
              this.sportMeta = bom.find(m => m.sport_league.toUpperCase() === this.league.toUpperCase());
              this.trustedDescription = this.sanitiser.bypassSecurityTrustHtml(this.sportMeta.app_page_subheader)
              this.trustedCopy = this.sanitiser.bypassSecurityTrustHtml(this.sportMeta.subheading_description);

              
              this.metaSetter.setMeta({
                title: (this.sportMeta.seo_title ? this.sportMeta.seo_title : this.sportMeta.app_title),
                seoTitle: this.sportMeta.seo_title ? this.sportMeta.seo_title : this.sportMeta.app_title,
                description: this.sportMeta.seo_description,
                metaImageURL: this.pageMeta.thumbnail.url,
                canonicalURL: `${environment.canonicalDomain}/best-odds?league=${this.league.toLowerCase()}&val=${this.route.snapshot.queryParamMap.get("val")?.toLowerCase()}`,
              });

            } else {
              this.sportMeta = null;
            }

          })

      })

  }

  selectLeague(data: Event) {
    const target = data.target as HTMLSelectElement;
    this.router.navigateByUrl(`best-odds?league=${this.renameLeagueReverse(
      target.options[target.selectedIndex].text
        ? target.options[target.selectedIndex].text
        : 'mlb'
    )}&val=${target.value}`)
  }
  renameLeague(league: string) {
    league = league || 'mlb'
    league = league.toLowerCase()
    switch (league) {
      case 'cbb':
        return 'ncaab'
      case 'cfb':
        return 'ncaaf'
      case 'esp':
        return 'la-liga'
      case 'bun':
        return 'bundesliga';
      case 'sra':
        return 'serie-a';
      case 'lun':
        return 'ligue-1';
      case 'lmx':
        return 'liga-mx';
      default:
        return league
    }
  }
  renameLeagueReverse(league: string) {
    league = league.toLowerCase()
    switch (league) {
      case 'ncaab':
        return 'cbb'
      case 'ncaaf':
        return 'cfb'
      case 'la liga':
        return 'esp'
      case 'bundesliga':
        return 'bun';
      case 'serie a':
        return 'sra';
      case 'ligue 1':
        return 'lun';
      case 'liga mx':
        return 'lmx';
      default:
        return league
    }
  }
  isValidState(state) {
    state = state.toLowerCase()
    switch (state) {
      case 'co':
        return true
      case 'ia':
        return true
      case 'il':
        return true
      case 'in':
        return true
      case 'mi':
        return true
      case 'nj':
        return true
      case 'pa':
        return true
      case 'tn':
        return true
      case 'va':
        return true
      case 'wv':
        return true
      default:
        return false
    }
  }

}
